import { Activity } from "lucide-react";
import { useMemo } from "react";

import { ActiveAttemptBanner } from "@/components/active-attempt-banner";
import { ChallengeShell } from "@/components/challenge/challenge-shell";
import { Tile } from "@/components/challenge/challenge-parts";
import { SdkSnippet } from "@/components/sdk-snippet";
import { SessionRules } from "@/components/session-rules";
import { useRefreshing, type AsyncState } from "@/hooks/use-async";
import { useEnrolment } from "@/hooks/use-enrolment";
import type { Challenge, SimQuota, SimSession } from "@/lib/api";
import { studentApi } from "@/lib/api";
import { cn } from "@/lib/utils";

/** Poll rates for the quota and session list: brisk while something is going on. */
const ACTIVE_POLL_MS = 2000;
const IDLE_POLL_MS = 15000;

/**
 * A challenge the server simulates, reached with the SDK over a WebSocket session.
 *
 * The unit here is the attempt -- a session -- and the page keeps an eye on them: a banner
 * when one of the team's is running or reserved, and a "My attempts" tab listing every one,
 * scored or not. The live view itself is the attempt's own page, one click from the banner.
 *
 * There is no "Submit a run" button on purpose. A session *is* the submission; the student
 * calls `checkin()` and the score exists. Offering an upload button would send them looking for
 * a file that no longer gets written.
 */
export function SimChallengePage({ challenge }: { challenge: Challenge }) {
  const { apiKey, enrolled } = useEnrolment(challenge);

  // One poll for both, since they change together: a session appearing in the list is the
  // same event as the quota's `running` going up. Keyed on the challenge as well as the key
  // so switching challenges cannot show the previous one's numbers for a frame. Gated on
  // enrolment: quota is per student per challenge, and the server refuses to discuss one
  // for a course the key is not in.
  const state = useRefreshing(
    apiKey && enrolled
      ? async () => {
          const [quota, sessions] = await Promise.all([
            studentApi.simQuota(challenge.id, apiKey),
            studentApi.simSessions(challenge.id, apiKey),
          ]);
          return { quota, sessions };
        }
      : null,
    ({ quota, sessions }) =>
      quota.running > 0 || quota.reservation || sessions.some((s) => s.status === "running")
        ? ACTIVE_POLL_MS
        : IDLE_POLL_MS,
    [challenge.id, apiKey, enrolled],
  );
  const quota = state.data?.quota ?? null;
  const sessions = useMemo<AsyncState<SimSession[]>>(
    () => ({
      data: state.data?.sessions ?? null,
      error: state.error,
      loading: state.loading,
      refetch: state.refetch,
    }),
    [state.data, state.error, state.loading, state.refetch],
  );

  return (
    <ChallengeShell
      challenge={challenge}
      banner={
        <ActiveAttemptBanner
          challenge={challenge}
          quota={quota}
          sessions={sessions.data}
        />
      }
      attempts={apiKey && enrolled ? sessions : undefined}
      extraTile={<AttemptsTile challenge={challenge} quota={quota} />}
      setupSummary="Runs on our server. Point the kit's .env at this challenge and run your agent."
      setup={
        <div
          className={cn(
            "grid gap-4",
            quota && "lg:grid-cols-[minmax(0,1fr)_16rem]",
          )}
        >
          <SdkSnippet challenge={challenge} />
          {quota ? <SessionRules quota={quota} /> : null}
        </div>
      }
    />
  );
}

/**
 * Attempts, with the caller's own count once it is known.
 *
 * Anonymous readers see the challenge's limits; an enrolled student sees where they
 * stand against them, so the number they need before connecting is in the same row as
 * the deadline rather than in a card of its own.
 */
function AttemptsTile({
  challenge,
  quota,
}: {
  challenge: Challenge;
  quota: SimQuota | null;
}) {
  // Noise changes what a controller has to cope with, so it sits with the other rules.
  const steps = (
    <span className="text-muted-foreground">
      {" · "}
      {challenge.max_steps.toLocaleString()} steps
    </span>
  );

  // A competition's budget is the team's; practice is each student's own.
  const per = challenge.type === "competition" ? "team" : "student";

  if (!quota) {
    return (
      <Tile icon={<Activity className="size-3.5" aria-hidden />} label="Attempts">
        {challenge.max_attempts === null ? "Unlimited" : `${challenge.max_attempts} per ${per}`}
        {steps}
      </Tile>
    );
  }

  const allowed = quota.attempts_allowed;
  const left = allowed === null ? null : Math.max(0, allowed - quota.attempts_used);
  const exhausted = left === 0;
  const whose = quota.attempts_per === "team" ? " · your team's" : "";

  return (
    <Tile
      icon={<Activity className="size-3.5" aria-hidden />}
      label="Attempts"
      tone={exhausted ? "danger" : undefined}
    >
      {allowed === null
        ? `${quota.attempts_used} used · unlimited`
        : `${left} left of ${allowed}${whose}`}
      {steps}
    </Tile>
  );
}
